import React, { useCallback } from 'react'
import { useScrollIntent } from '../../hooks/useScrollIntent'

/**
 * Wraps interactive content inside a React Flow node to isolate it from
 * the canvas event system (drag, pan, scroll, click propagation).
 *
 * Replaces the repeated pattern of:
 *   onClick={stopPropagation}
 *   onKeyDown={stopPropagation}
 *   onMouseDown={stopPropagation}
 *   className="nodrag"
 *
 * Props:
 * - `scrollable` — enables smart scroll-intent detection so the zone only
 *   captures wheel events when the user started scrolling inside it
 * - `nopan` — also prevents React Flow panning (for text areas etc.)
 */

interface NodeInteractionZoneProps {
  children: React.ReactNode
  scrollable?: boolean
  nopan?: boolean
  className?: string
  style?: React.CSSProperties
}

export const NodeInteractionZone = React.memo(
  ({ children, scrollable = false, nopan = false, className, style }: NodeInteractionZoneProps) => {
    const { containerRef, shouldCaptureWheel } = useScrollIntent()

    const stopPropagation = useCallback((e: React.SyntheticEvent) => {
      e.stopPropagation()
    }, [])

    const classes = ['nodrag']
    if (nopan) classes.push('nopan')
    if (scrollable && shouldCaptureWheel) classes.push('nowheel')
    if (className) classes.push(className)

    return (
      <div
        ref={scrollable ? containerRef : undefined}
        className={classes.join(' ')}
        style={style}
        onClick={stopPropagation}
        onKeyDown={stopPropagation}
        onMouseDown={stopPropagation}
      >
        {children}
      </div>
    )
  }
)
